import {INTERNAL_FLOWCHART_ACTION_FUNCTIONS} from './internal-action-functions/ActionFunctions';

export enum AnalysisFlowchartAction {
  /**
   * Add a new source step. Will call `PipelineDAO.addAndUpdatePipelineSteps`.
   */
  ADD_SOURCE_STEP = 'ADD_SOURCE_STEP',
  /**
   * Connect or disconnect steps. Will call `PipelineDAO.addAndUpdatePipelineSteps`.
   */
  CONNECT_STEPS = 'CONNECT_STEPS',
  /**
   * Delete a step, and attempt to reparent it. Will call `PipelineDAO.deletePipelineSteps`.
   */
  DELETE_STEP = 'DELETE_STEP',
  /**
   * Delete a step and all following steps. Will call `PipelineDAO.deletePipelineSteps`.
   */
  DELETE_STEP_AND_CHILDREN = 'DELETE_STEP_AND_CHILDREN',
  /**
   * Duplicate steps. Steps will have the same inputs, configs, and statuses. Existing steps will not be
   * modified.
   *
   * Will call `PipelineDAO.copyPipelineSteps`.
   */
  DUPLICATE_STEPS = 'DUPLICATE_STEPS',
  /**
   * Open a menu of step types, to add a step.
   *
   * Parents of <AnalysisFlowchart> are responsible for showing the step type options, through `onAction`.
   * See `AddStepRightSidebar.tsx`.
   */
  OPEN_ADD_STEP = 'OPEN_ADD_STEP',
  /**
   * Open the add step sidebar for adding a source step (no parent connection).
   *
   * Parents of <AnalysisFlowchart> are responsible for showing the step type options, through `onAction`.
   * See `AddStepRightSidebar.tsx`.
   */
  OPEN_ADD_SOURCE_STEP = 'OPEN_ADD_SOURCE_STEP',
  /**
   * Open a step's configuration.
   *
   * Parents of <AnalysisFlowchart> are responsible for showing the step configuration, through `onAction`.
   */
  OPEN_STEP_CONFIGURATION = 'OPEN_STEP_CONFIGURATION',
  /**
   * Re-run a step. Will call `PipelineDAO.runPipelineStep`.
   */
  RERUN_STEP = 'RERUN_STEP',
}

/**
 * The value returned to the parent of <AnalysisFlowchart>, through the optional `onAction` callback.
 */
export type ActionReturnType<T extends AnalysisFlowchartAction> = Awaited<
  ReturnType<(typeof INTERNAL_FLOWCHART_ACTION_FUNCTIONS)[T]>
>;

/**
 * Optional information, passed from the parent of <AnalysisFlowchart>, to enable interactive actions.
 *
 * See `AnalysisFlowchartAction` for the available actions, and to understand if the parent of
 * <AnalysisFlowchart> should implement an `onAction` callback.
 */
export interface AnalysisFlowchartAvailableAction<
  T extends AnalysisFlowchartAction = AnalysisFlowchartAction,
> {
  /**
   * The action to make available in the flowchart.
   */
  action: T;
  /**
   * Called after the action has been completed.
   */
  onAction?: (arg: ActionReturnType<T>) => void;
}
